import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import MainLayout from '../../components/layout/MainLayout';

const WarmtepompPage = () => {
  const specialists = [
    {
      name: 'WarmtePomp Pro',
      rating: 4.9,
      reviews: 143,
      location: 'Den Haag',
      specialization: 'Lucht-water warmtepompen',
      experience: '15+ jaar'
    },
    {
      name: 'EcoHeat Installaties',
      rating: 4.8,
      reviews: 112,
      location: 'Eindhoven',
      specialization: 'Bodem warmtepompen',
      experience: '10+ jaar'
    },
    {
      name: 'Duurzaam Verwarmen',
      rating: 4.7,
      reviews: 89,
      location: 'Groningen',
      specialization: 'Hybride systemen',
      experience: '8+ jaar'
    }
  ];

  const opdrachten = [
    {
      title: 'Lucht-water warmtepomp installatie',
      location: 'Den Haag',
      budget: '€12.000 - €18.000',
      deadline: '3 weken',
      description: 'Vervanging CV-ketel door moderne warmtepomp, woning 150m²'
    },
    {
      title: 'Bodem warmtepomp nieuwbouw',
      location: 'Almere',
      budget: '€25.000 - €35.000',
      deadline: '2 maanden',
      description: 'Complete installatie met vloerverwarming voor nieuwbouwwoning'
    }
  ];

  return (
    <MainLayout>
      <div className="min-h-screen bg-background">
        <motion.section 
          className="bg-gradient-to-br from-blue-50 to-cyan-50 py-16 md:py-24"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
              <Icon name="ArrowLeft" size={16} />
              Terug naar home
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
                <Icon name="Thermometer" size={32} className="text-blue-600" />
              </div>
              <h1 className="text-4xl md:text-5xl font-heading font-bold text-foreground">
                Warmtepomp Installatie
              </h1>
            </div>
            <p className="text-lg text-muted-foreground max-w-3xl">
              Duurzaam verwarmen met professionele warmtepomp installaties. Lucht-water, bodem en hybride systemen.
            </p>
          </div>
        </motion.section>

        <section className="py-12 border-b border-border">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { label: 'Actieve opdrachten', value: '34', icon: 'Briefcase' },
                { label: 'Specialisten', value: '98', icon: 'Users' },
                { label: 'Gemiddelde rating', value: '4.8', icon: 'Star' },
                { label: 'Voltooide projecten', value: '850+', icon: 'CheckCircle' }
              ]?.map((stat, index) => (
                <motion.div
                  key={index}
                  className="text-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Icon name={stat?.icon} size={24} className="text-primary mx-auto mb-2" />
                  <div className="text-3xl font-bold text-foreground mb-1">{stat?.value}</div>
                  <div className="text-sm text-muted-foreground">{stat?.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <h2 className="text-3xl font-heading font-semibold text-foreground mb-8">Actuele Opdrachten</h2>
            <div className="grid gap-6">
              {opdrachten?.map((opdracht, index) => (
                <motion.div
                  key={index}
                  className="bg-white border-2 border-slate-200 rounded-xl p-6 transition-all"
                  style={{
                    boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
                  }}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-foreground mb-2">{opdracht?.title}</h3>
                      <p className="text-muted-foreground mb-3">{opdracht?.description}</p>
                      <div className="flex flex-wrap gap-4 text-sm">
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <Icon name="MapPin" size={16} />
                          {opdracht?.location}
                        </span>
                        <span className="flex items-center gap-1 text-primary font-medium">
                          <Icon name="DollarSign" size={16} />
                          {opdracht?.budget}
                        </span>
                      </div>
                    </div>
                    <Link to="/opdracht-zoeken" className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors whitespace-nowrap">
                      Bekijk details
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <h2 className="text-3xl font-heading font-semibold text-foreground mb-8">Top Specialisten</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {specialists?.map((specialist, index) => (
                <motion.div
                  key={index}
                  className="bg-white border-2 border-slate-200 rounded-xl p-6 transition-all"
                  style={{
                    boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.05 }}
                >
                  <h3 className="font-semibold text-foreground mb-3">{specialist?.name}</h3>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Icon name="Star" size={14} className="text-amber-500" />
                      {specialist?.rating} ({specialist?.reviews} reviews)
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name="MapPin" size={16} />
                      {specialist?.location}
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name="Award" size={16} />
                      {specialist?.specialization}
                    </div>
                  </div>
                  <Link to="/contractor-job-browser" className="mt-4 w-full block text-center px-4 py-2 border border-primary text-primary rounded-lg hover:bg-primary hover:text-white transition-colors">
                    Bekijk profiel
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 lg:px-6 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl p-8 md:p-12"
            >
              <Icon name="Thermometer" size={48} className="text-primary mx-auto mb-4" />
              <h2 className="text-3xl font-heading font-bold text-foreground mb-4">Klaar om te starten?</h2>
              <p className="text-lg text-muted-foreground mb-8">Plaats uw opdracht en ontvang offertes van warmtepomp specialisten</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/opdracht-plaatsen" className="px-8 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium">
                  Opdracht plaatsen
                </Link>
                <Link to="/opdracht-zoeken" className="px-8 py-3 border border-border bg-card text-foreground rounded-lg hover:bg-muted transition-colors font-medium">
                  Alle opdrachten bekijken
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </MainLayout>
  );
};

export default WarmtepompPage;